import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, ComponentSync, getElementCenterPos, LL_H_C, LL_V_L, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, openContent, Show, TS_Checkbox, TS_Input, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { ICONS } from '@app/styles';
import { variableType_AttributeInstance, variableType_EvidenceOf, variableType_FamilyHistoryOf, variableType_HistoryOf, variableType_Normal, variableType_Recent } from '@app/hcs-shared';
import { DropDown_Values, ModuleFE_ContextRule, ModuleFE_Variable, Q_SortableList } from '@app/hcs-frontend';
import { arrayIncludesAll, dbObjectToId, Filter, filterDuplicates, sortArray } from '@nu-art/ts-common';
import './Editor_SnippetFormVariables.scss';
const allowedVarTypesForFormVar = [
    variableType_Normal,
    variableType_HistoryOf,
    variableType_FamilyHistoryOf,
    variableType_EvidenceOf,
    variableType_Recent
];
export class Editor_SnippetFormVariables extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.snippet = nextProps.snippet;
        state.editMode = nextProps.editMode;
        return state;
    }
    // ######################## Logic ########################
    getVariableName = (_variable) => {
        const variable = typeof _variable === 'string' ? ModuleFE_Variable.cache.unique(_variable) : _variable;
        if (!variable)
            return 'N/A';
        return variable.name.includes('.') ? variable.name.split('.')[1] : variable.name;
    };
    getVariablesForRule = (rule) => {
        return ModuleFE_Variable.cache.filter(variable => {
            if (rule.variableTypes?.length && !rule.variableTypes.includes(variable.type))
                return false;
            return arrayIncludesAll(variable._tagIds, rule.tagIds);
        });
    };
    getAggregatedVariables = () => {
        const dbContextRules = ModuleFE_ContextRule.cache.unique(this.state.snippet.item.contextRuleId);
        const rules = dbContextRules?.rules ?? [];
        const collected = rules.map(rule => this.getVariablesForRule(rule)).flat();
        return filterDuplicates(collected, dbObjectToId);
    };
    openFormVariableAdditionPopUp = (e) => {
        const model = {
            id: 'advisor-context-snippet-pop-up',
            originPos: getElementCenterPos(e.currentTarget),
            modalPos: { x: 1, y: 1 },
            offset: { x: 10 },
            content: () => {
                const snippet = this.state.snippet;
                let availableVariables = this.getAggregatedVariables().filter(variable => {
                    if (variable.type !== variableType_Normal)
                        return false;
                    return !snippet.item.formVariables?.find(item => item.id === variable._id);
                });
                availableVariables = filterDuplicates(availableVariables);
                sortArray(availableVariables, variable => variable._nameInLowercase);
                return _jsx(_Fragment, { children: availableVariables.map(variable => {
                        return _jsx("div", { onClick: () => {
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                                const formVars = [...(snippet.item.formVariables ?? [])];
                                formVars.push({ id: variable._id });
                                return snippet.updateObj({ formVariables: formVars });
                            }, children: variable.name }, variable._id);
                    }) });
            }
        };
        ModuleFE_MouseInteractivity.showContent(model);
    };
    addAllVariablesToFormVariables = async () => {
        const snippet = this.state.snippet;
        let availableVariables = this.getAggregatedVariables().filter(variable => {
            if (!allowedVarTypesForFormVar.includes(variable.type))
                return;
            return !snippet.item.formVariables?.find(item => item.id === variable._id);
        });
        availableVariables = filterDuplicates(availableVariables);
        sortArray(availableVariables, variable => variable._nameInLowercase);
        const formVars = [...(snippet.item.formVariables ?? [])];
        formVars.push(...availableVariables.map(variable => ({ id: variable._id })));
        await snippet.updateObj({ formVariables: formVars });
    };
    getToolTip = (text) => {
        return openContent.tooltip.top('snippet-form-variables-tooltip', text, { offset: 10 });
    };
    getSelectedFormVar = () => {
        const formVars = this.state.snippet.editProp('formVariables', []);
        const selectedFormVarIndex = formVars.item.findIndex(formVar => formVar?.id === this.state.selectedFormVarId);
        const formVar = selectedFormVarIndex !== -1 ? formVars.editProp(selectedFormVarIndex, {}) : undefined;
        formVar?.setOnDelete(async () => {
            await formVars.removeArrayItem(selectedFormVarIndex);
            this.setState({ selectedFormVarId: undefined });
        });
        formVar?.setOnChanged(async () => this.forceUpdate());
        return formVar;
    };
    getAttributesForVariable = (formVariable) => {
        return ModuleFE_Variable.cache.filter(variable => {
            if (variable.type !== variableType_AttributeInstance)
                return false;
            if (variable.parents.variableId !== formVariable.id)
                return false;
            return !formVariable.attributes?.find(item => item.id === variable._id);
        });
    };
    openAddAttribute = (e, formVar) => {
        const model = {
            id: 'advisor-context-snippet-pop-up',
            originPos: getElementCenterPos(e.currentTarget),
            modalPos: { x: 1, y: 1 },
            offset: { x: 10 },
            content: () => {
                let attributes = this.getAttributesForVariable(formVar.item);
                if (!attributes.length)
                    return _jsx("div", { children: "No more attributes to add for this variable!" });
                attributes = sortArray(attributes, '_nameInLowercase');
                return attributes.map(attribute => {
                    const name = this.getVariableName(attribute);
                    return _jsx("div", { onClick: () => {
                            ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                            const attributes = [...(formVar.item.attributes ?? [])];
                            attributes.push({ id: attribute._id });
                            return formVar.updateObj({ attributes });
                        }, children: name }, attribute._id);
                });
            }
        };
        ModuleFE_MouseInteractivity.showContent(model);
    };
    // ######################## Render ########################
    render() {
        return _jsx(TS_PropRenderer.Vertical, { label: this.render_Header(), className: 'editor__snippet-form-variables', children: _jsxs(LL_H_C, { className: 'editor__snippet-form-variables__body', children: [this.render_List(), this.render_Editor()] }) });
    }
    render_Header = () => {
        const snippet = this.state.snippet;
        return _jsx(LL_H_C, { style: { font: 'inherit', gap: '10px' }, children: _jsxs(Show, { children: [_jsx(Show.If, { condition: true, children: "Form Variables" }), _jsx(Show.If, { condition: true, children: this.render_Search() }), _jsxs(Show.If, { condition: this.state.editMode, children: [_jsx(ICONS.advisor_v5_plus.component, { onClick: this.openFormVariableAdditionPopUp }), _jsxs("div", { className: 'action-icon', ...this.getToolTip('Add all variables that pass the rules to the list'), onClick: this.addAllVariablesToFormVariables, children: ["A", _jsx("sup", { children: "A" })] }), _jsxs("div", { className: 'action-icon', ...this.getToolTip('Clear list'), onClick: () => {
                                    snippet.updateObj({ formVariables: [] });
                                }, children: ["R", _jsx("sup", { children: "A" })] })] }), _jsx(Show.If, { condition: true, children: _jsx(ICONS.information.component, { style: { cursor: 'help' }, ...this.getToolTip('If this snippet is part of a wizard step that is being rendered as a form, limit it to only these variables') }) })] }) });
    };
    render_Search = () => {
        return _jsxs(LL_H_C, { className: 'editor__snippet-form-variables__search', children: [_jsx(TS_Input, { type: 'text', value: this.state.searchTerm, onChange: searchTerm => this.setState({ searchTerm }) }), _jsx(ICONS.search.component, {})] });
    };
    render_List = () => {
        const snippet = this.state.snippet;
        const filter = this.state.searchTerm?.length ? new Filter(i => [i.name]) : undefined;
        return _jsx(Q_SortableList, { disabled: !this.state.editMode, items: [...(snippet.item.formVariables ?? [])], itemRenderer: formVariable => {
                const variable = ModuleFE_Variable.cache.unique(formVariable.id);
                if (variable && filter && !filter.filterItem(variable, this.state.searchTerm))
                    return;
                const className = _className('editor__snippet-form-variables__form-var', formVariable.id === this.state.selectedFormVarId && 'selected');
                return _jsxs(LL_H_C, { className: className, onClick: () => this.setState({ selectedFormVarId: formVariable.id }), children: [_jsx("span", { className: 'editor__snippet-form-variables__form-var__name', children: variable?.name }), formVariable.attributes?.length && _jsxs("span", { className: 'editor__snippet-form-variables__form-var__att-count', children: ["(", formVariable.attributes.length, ")"] }), formVariable.isCommon &&
                            _jsx("span", { className: 'editor__snippet-form-variables__form-var__is-common', children: "C" })] });
            }, onChange: variables => {
                return snippet.updateObj({ formVariables: variables });
            } });
    };
    render_Editor = () => {
        const formVar = this.getSelectedFormVar();
        return _jsxs(LL_V_L, { className: 'editor__snippet-form-variables__editor', children: [this.render_Editor_Header(formVar), this.render_Editor_IsCommon(formVar), this.render_Editor_DefaultValue(formVar), this.render_Editor_Attributes(formVar)] });
    };
    // ######################## Render - Editor Parts ########################
    render_Editor_Header = (formVar) => {
        if (!formVar)
            return;
        const variable = ModuleFE_Variable.cache.unique(formVar.item.id);
        return _jsx(LL_H_C, { className: 'editor__snippet-form-variables__editor__header', children: _jsxs(Show, { children: [_jsx(Show.If, { condition: true, children: variable?.name ?? 'N/A' }), _jsx(Show.If, { condition: this.state.editMode, children: _jsx(ICONS.bin.component, { onClick: () => formVar.delete() }) })] }) });
    };
    render_Editor_IsCommon = (formVar) => {
        if (!formVar)
            return;
        const checked = formVar.item.isCommon;
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Is Common', children: _jsx(TS_Checkbox, { checked: checked, onCheck: () => {
                    formVar.updateObj({ isCommon: !checked });
                } }) });
    };
    render_Editor_DefaultValue = (formVar) => {
        if (!formVar?.item.isCommon)
            return;
        const variable = ModuleFE_Variable.cache.unique(formVar.item.id);
        if (!variable) {
            this.logError(`Could not find variable for id ${formVar.item.id}`);
            return;
        }
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Default Value', children: _jsx(DropDown_Values, { variable: variable, canUnselect: true, selected: formVar.item.defaultValueId, onSelected: value => formVar.updateObj({ defaultValueId: value?._id }) }) });
    };
    render_Editor_Attributes = (formVar) => {
        if (!formVar)
            return;
        const label = () => _jsxs(_Fragment, { children: ["Attributes", this.state.editMode &&
                    _jsx(ICONS.advisor_v5_plus.component, { onClick: e => this.openAddAttribute(e, formVar) })] });
        const attributes = formVar.item.attributes ?? [];
        return _jsx(TS_PropRenderer.Vertical, { label: label, className: 'editor__snippet-form-variables__editor__attributes', children: _jsx(Q_SortableList, { disabled: !this.state.editMode, items: [...attributes], onChange: attributes => formVar.updateObj({ attributes }), itemRenderer: (attribute, index) => {
                    const name = this.getVariableName(attribute.id);
                    return _jsxs(_Fragment, { children: [name, this.state.editMode &&
                                _jsx(ICONS.bin.component, { onClick: () => {
                                        const attributes = formVar.editProp('attributes', []);
                                        attributes.removeArrayItem(index);
                                    } })] });
                } }) });
    };
}
//# sourceMappingURL=Editor_SnippetFormVariables.js.map